import CircleQuestionFilled from '@app/assets/svg/CircleQuestionFilled';
import handleApiErr from '@app/helper/handleApiErr';
import ScreenWrapper from '@app/hoc/ScreenWrapper';
import ROUTE from '@app/navigationApp/routes';
import { PrivateStackParamList } from '@app/navigationApp/types';
import { useIsEnhanceLimit } from '@app/hooks/useIsEnhanceLimit';
import Button from '@app/sharedComponents/Button/Button';
import CommonHeader from '@app/sharedComponents/Header/Header';
import { CaretRight } from '@app/sharedComponents/Icons/Icons';
import PageLoader from '@app/sharedComponents/PageLoader/PageLoader';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { showErrorSheet } from '@app/store/slices/errorSlice';
import { Theme, ThemedText, ThemedView } from '@app/theme';
import { formatIndianCurrency } from '@app/utils/utility';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useTheme } from '@shopify/restyle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, ScrollView, TouchableOpacity } from 'react-native';
import { trackEvent } from '@app/utils';
import {
  getExperimentCohort,
  getInterestRateProps,
  getRecommendedOffer,
} from '@app/utils/creditFlowAnalytics';
import CreditLimitAmountInput from './Components/CreditLimitAmountInput/CreditLimitAmountInput';
import LimitSlider from './Components/LimitSlider/LimitSlider';
import { useCreditLimitAmount } from './hooks/useCreditLimitAmount';
import InterestRateLearnMoreModal from './Components/InterestRateLearnMoreModal/InterestRateLearnMoreModal';
import {
  getLimitConfig,
  getMonthlyEmi,
} from './utils';
import { pledgeSaveV2APICall } from '@screens/Portfolio/services/services';
import { getApplicationId } from '@screens/FetchPortfolio/utils';

type ChooseLimitProps = NativeStackScreenProps<
  PrivateStackParamList,
  'SET_CREDIT_LIMIT'
>;

const ChooseLimit = ({ navigation, route }: ChooseLimitProps) => {
  const { colors } = useTheme<Theme>();
  const dispatch = useAppDispatch();
  const { isFromEnhanceLimit } = useIsEnhanceLimit();
  const { userDetails } = useAppSelector((state) => state.auth);
  const { minimumEligibleLimit, authCAS } = useAppSelector(
    (state) => state.application,
  );

  const authCASLoose = authCAS as any;
  const pledgeOffers = authCASLoose?.offers;
  const availableCreditAmount =
    authCASLoose?.availableCreditLimit ?? authCAS?.availableCreditAmount;
  const isPledgeReady = !!authCAS && availableCreditAmount !== undefined;

  const limitConfig = useMemo(
    () =>
      getLimitConfig(
        minimumEligibleLimit?.amount,
        availableCreditAmount,
        pledgeOffers,
      ),
    [minimumEligibleLimit?.amount, availableCreditAmount, pledgeOffers],
  );

  const {
    selectedAmount,
    onSliderAmountChange,
    flushSliderDrag,
    amountInput,
    setAmountInput,
    amountValidationError,
    applyAmount,
    isEditingInput,
  } = useCreditLimitAmount(limitConfig, limitConfig.rateThreshold);

  const [showInterestLearnMore, setShowInterestLearnMore] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  // Common analytics property bundle for all SETLIMIT events on this screen.
  const buildSetLimitProps = useCallback(
    () => ({
      max_eligible_credit_limit: limitConfig.maxAmount,
      selected_credit_limit: selectedAmount,
      recommended_offer: getRecommendedOffer(
        authCASLoose?.selectedOfferCode,
        pledgeOffers,
      ),
      interest_rate: getInterestRateProps(limitConfig),
      experiment_cohort: getExperimentCohort(),
      source_page: route.name,
    }),
    [limitConfig, selectedAmount, route.name],
  );

  const pageViewedRef = useRef(false);
  useEffect(() => {
    if (!isPledgeReady || pageViewedRef.current) return;
    pageViewedRef.current = true;
    trackEvent('SET_MF_LIMIT_AMOUNT_PAGE_VIEWED', buildSetLimitProps());
  }, [isPledgeReady, buildSetLimitProps]);

  const handleSliderComplete = useCallback(() => {
    flushSliderDrag();
    trackEvent('SETLIMIT_SLIDER_CLICKED', buildSetLimitProps());
  }, [flushSliderDrag, buildSetLimitProps]);

  const handleEditSelectedFunds = useCallback(() => {
    trackEvent('SETLIMIT_EDITSELECTION_CLICKED', buildSetLimitProps());
    navigation.navigate(ROUTE.PORTFOLIO_V1, { requestCreditLimit: selectedAmount });
  }, [selectedAmount, navigation, buildSetLimitProps]);

  const handleProceed = useCallback(() => {
    if (amountValidationError || btnLoading) {
      return;
    }

    trackEvent('SETLIMIT_CONFIRM&PROCEED_CLICKED', buildSetLimitProps());

    const savePledgeLimit = async () => {
      setBtnLoading(true);
      try {
        const applicationId = await getApplicationId(
          userDetails?.linkedApplications?.[0]?.applicationId ?? '',
        );
        await pledgeSaveV2APICall({
          applicationId,
          action: 'SAVE_LIMIT',
          requestCreditLimit: selectedAmount,
        });

        if (isFromEnhanceLimit) {
          navigation.navigate(ROUTE.LOAN_OFFER_ENHANCE_LIMIT, {
            source: 'CHOOSE_CREDIT_LIMIT_V1',
          });
        } else {
          navigation.navigate(ROUTE.PLEDGE_CONFIRMATION, {});
        }
      } catch (error: any) {
        if (error?.data?.statusCode && handleApiErr(error)) {
          dispatch(
            showErrorSheet({
              isErrorSheetVisible: true,
              errorMessage: error?.data?.message,
              closeOnBackdropPress: true,
              errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
              errorHeading: error?.data?.header,
              showErrorHeading: true,
              btnName: 'Ok, got it!',
            }),
          );
        }
      } finally {
        setBtnLoading(false);
      }
    };

    savePledgeLimit();
  }, [
    amountValidationError,
    btnLoading,
    selectedAmount,
    dispatch,
    isFromEnhanceLimit,
    navigation,
    userDetails?.linkedApplications,
    buildSetLimitProps,
  ]);

  return (
    <ScreenWrapper screenName={route.name}>
      <ThemedView flex={1} backgroundColor="bg-surface-page">
        <CommonHeader
          navigation={navigation}
          pageName={route.name}
          showNeedHelp
          backEventName="SETLIMIT_BACK_CLICKED"
          backEventProperties={buildSetLimitProps()}
        />

        {!isPledgeReady ? (
          <ThemedView flex={1} justifyContent="center" alignItems="center">
            <PageLoader />
          </ThemedView>
        ) : (
          <>
        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: 16, paddingBottom: 24 }}
        >
          <ThemedText variant="h3b" color="text-primary" mb="sm">
            Choose your limit
          </ThemedText>
          <ThemedText variant="b3r" color="text-secondary" mb="xl">
            you will be able to withdraw up to this limit
          </ThemedText>

          <ThemedView
            backgroundColor="bg-surface-primary"
            borderRadius="lg"
            borderWidth={1}
            borderColor="border-primary"
            px="lg"
            py="2xl"
            style={[
              { overflow: 'visible' },
              Platform.OS === 'ios'
                ? {
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.06,
                    shadowRadius: 10,
                  }
                : { elevation: 1 },
            ]}
          >
            <ThemedText
              variant="b5m"
              color="text-secondary"
              textAlign="center"
              mb="sm"
            >
              SELECTED CREDIT LIMIT
            </ThemedText>

            <CreditLimitAmountInput
              limitConfig={limitConfig}
              amountInput={amountInput}
              setAmountInput={setAmountInput}
              applyAmount={applyAmount}
              isEditingInput={isEditingInput}
              marginBottom={amountValidationError ? 'sm' : 'lg'}
              onEditPress={() =>
                trackEvent('EDIT_AMOUNT_BUTTON_CLICKED', buildSetLimitProps())
              }
              onAmountConfirm={() =>
                trackEvent(
                  'SETLIMIT_EDITAMOUNT_AMOUNTCONFIRM_CLICKED',
                  buildSetLimitProps(),
                )
              }
            />

            {amountValidationError ? (
              <ThemedText
                variant="b4r"
                color="text-error"
                textAlign="center"
                mb="lg"
              >
                {amountValidationError}
              </ThemedText>
            ) : null}

            <ThemedView alignItems="center" mb="2xl">
              <ThemedView
                backgroundColor="bg-fill-emphasis-primary-1"
                px="lg"
                py="sm"
                borderRadius="full"
                // borderColor="border-blue-light"
                // borderWidth={1}
              >
                <ThemedText variant="b4r" color="text-action-primary">
                  Interest only EMI: ₹
                  {formatIndianCurrency(
                    getMonthlyEmi(selectedAmount, limitConfig),
                  )}
                  /Month
                </ThemedText>
              </ThemedView>
            </ThemedView>

            <LimitSlider
              limitConfig={limitConfig}
              selectedAmount={selectedAmount}
              onAmountChange={onSliderAmountChange}
              enableNudge
              onSlideComplete={handleSliderComplete}
            />

            <ThemedText
              variant="b5r"
              color="text-secondary"
              textAlign="center"
              mt="lg"
            >
              Drag to adjust your credit limit
            </ThemedText>
          </ThemedView>

          <TouchableOpacity
            onPress={() => {
              trackEvent(
                'SETLIMIT_INTERESTCHANGEINFOWIDGET_CLICKED',
                buildSetLimitProps(),
              );
              setShowInterestLearnMore(true);
            }}
          >
            <ThemedView
              mt="lg"
              backgroundColor="bg-surface-primary"
              borderRadius="md"
              borderWidth={1}
              borderColor="border-primary"
              p="sm"
              flexDirection="row"
              alignItems="center"
            >
              <ThemedView mr="xs">
                <CircleQuestionFilled size={24} color={colors['icon-emphasis-secondary']} />
              </ThemedView>
              <ThemedText variant="b3r" color="text-primary" flex={1}>
                Learn why your interest rate changes
              </ThemedText>
              <CaretRight color={colors['icon-secondary']} size={16} />
            </ThemedView>
          </TouchableOpacity>
        </ScrollView>

        <ThemedView
          backgroundColor="bg-fill-primary"
          px="lg"
          pt="lg"
          pb="2xl"
          borderTopWidth={1}
          borderTopColor="border-primary"
        >
          <Button
            label="Proceed"
            handlePress={handleProceed}
            type="SOLID"
            size="MD"
            bg="bg-action-primary"
            textColor="text-on-action-primary"
            disabled={!!amountValidationError || btnLoading}
            buttonLoader={btnLoading}
          />
          <TouchableOpacity onPress={handleEditSelectedFunds}>
            <ThemedText
              variant="b3s"
              color="text-action-primary"
              textAlign="center"
              mt="lg"
            >
              Edit selected funds
            </ThemedText>
          </TouchableOpacity>
        </ThemedView>

        <InterestRateLearnMoreModal
          visible={showInterestLearnMore}
          onClose={() => setShowInterestLearnMore(false)}
          routeName={route.name}
        />
          </>
        )}
      </ThemedView>
    </ScreenWrapper>
  );
};

export default ChooseLimit;
